import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { authApi } from "../api/services";

const ResetPassword = () => {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const token          = searchParams.get("token") || "";
  const [pageState, setPageState] = useState("verifying");
  const [email,     setEmail]     = useState("");
  const [password,        setPassword]        = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword,    setShowPassword]    = useState(false);
  const [isSubmitting,    setIsSubmitting]    = useState(false);
  const [passwordError,   setPasswordError]   = useState("");
  
  useEffect(() => {
    if (!token) {
      setPageState("invalid");
      return;
    }

    authApi.verifyResetToken(token)
      .then((res) => {
        setEmail(res.data?.data?.email || "");
        setPageState("valid");
      })
      .catch(() => {
        setPageState("invalid");
      });
  }, [token]);

  const getStrength = (pwd) => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd.length >= 8)                    score++;
    if (/[A-Z]/.test(pwd))                 score++;
    if (/[0-9]/.test(pwd))                 score++;
    if (/[^A-Za-z0-9]/.test(pwd))         score++;
    return score;   // 0-4
  };

  
  const strength     = getStrength(password);
  const strengthText = ["", "Weak", "Fair", "Good", "Strong"][strength];
  const strengthColor = [
    "",
    "bg-red-400",
    "bg-amber-400",
    "bg-blue-400",
    "bg-green-500",
  ][strength];

   const handleSubmit = async (e) => {
    e.preventDefault();
    setPasswordError("");

    if (password.length < 8) {
      setPasswordError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      await authApi.resetPassword({ token, password });
      setPageState("success");
      toast.success("Password reset! Please log in.");
      setTimeout(() => navigate("/", { replace: true }), 2500);
    } catch (err) {
      const msg = err.response?.data?.message || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderVerifying = () => (
    <div className="text-center py-8">
      <svg className="animate-spin w-10 h-10 text-blue-900 mx-auto mb-4"
           fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10"
                stroke="currentColor" strokeWidth="4"/>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
      </svg>
      <p className="text-sm text-gray-500">Validating your reset link…</p>
    </div>
  );

  const renderInvalid = () => (
    <div className="text-center py-4">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center
                      justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor"
             viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 8v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
        </svg>
      </div>
      <h2 className="text-lg font-bold text-gray-800 mb-2">Link expired or invalid</h2>
      <p className="text-sm text-gray-500 mb-6">
        This password-reset link has expired or has already been used.
        Reset links are only valid for <strong>1 hour</strong>.
      </p>
      <Link
        to="/forgot-password"
        className="inline-block px-5 py-2.5 bg-blue-900 text-white text-sm
                   font-semibold rounded-lg hover:bg-blue-800 transition-colors"
      >
        Request a New Link
      </Link>
    </div>
  );

  const renderSuccess = () => (
    <div className="text-center py-4">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center
                      justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor"
             viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M5 13l4 4L19 7"/>
        </svg>
      </div>
      <h2 className="text-lg font-bold text-gray-800 mb-2">Password updated!</h2>
      <p className="text-sm text-gray-500">Redirecting you to login…</p>
    </div>
  );

  const renderForm = () => (
    <>
      {email && (
        <p className="text-sm text-gray-500 mb-6">
          Setting a new password for{" "}
          <span className="font-semibold text-gray-700">{email}</span>
        </p>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">

        {/* New password */}
        <div>
          <label className="block text-xs font-semibold text-gray-500
                            uppercase tracking-wider mb-1.5">
            New Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setPasswordError(""); }}
              placeholder="Min. 8 characters"
              required
              autoFocus
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg
                         focus:outline-none focus:border-blue-900 focus:ring-1
                         focus:ring-blue-900 transition-colors text-sm pr-11"
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400
                         hover:text-gray-600 transition-colors"
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
              )}
            </button>
          </div>

          {/* Strength bar */}
          {password && (
            <div className="mt-2">
              <div className="flex gap-1 mb-1">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className={`h-1 flex-1 rounded-full transition-colors
                      ${i <= strength ? strengthColor : "bg-gray-200"}`}
                  />
                ))}
              </div>
              <p className={`text-xs font-medium
                ${strength <= 1 ? "text-red-500"
                  : strength === 2 ? "text-amber-500"
                  : strength === 3 ? "text-blue-600"
                  : "text-green-600"}`}>
                {strengthText}
              </p>
            </div>
          )}
        </div>

        {/* Confirm password */}
        <div>
          <label className="block text-xs font-semibold text-gray-500
                            uppercase tracking-wider mb-1.5">
            Confirm Password
          </label>
          <input
            type={showPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(""); }}
            placeholder="Repeat your password"
            required
            className={`w-full px-4 py-3 bg-gray-50 border rounded-lg
                       focus:outline-none focus:ring-1 transition-colors text-sm
                       ${confirmPassword && confirmPassword !== password
                         ? "border-red-300 focus:border-red-400 focus:ring-red-300"
                         : "border-gray-200 focus:border-blue-900 focus:ring-blue-900"
                       }`}
          />
          {confirmPassword && confirmPassword !== password && (
            <p className="text-xs text-red-500 mt-1">Passwords do not match.</p>
          )}
        </div>

        {/* Generic error */}
        {passwordError && (
          <p className="text-xs text-red-500">{passwordError}</p>
        )}

        <button
          type="submit"
          disabled={
            isSubmitting ||
            password.length < 8 ||
            password !== confirmPassword
          }
          className="w-full py-3 bg-blue-900 text-white font-bold rounded-lg shadow-md
                     hover:bg-blue-800 active:scale-95 transition-all
                     disabled:opacity-60 disabled:cursor-not-allowed text-sm"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10"
                        stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
              Saving…
            </span>
          ) : (
            "Set New Password"
          )}
        </button>
      </form>
    </>
  );

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <>
      <Toaster position="top-right" />

      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 font-sans">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">

            {/* Blue header strip */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-8 py-6">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center
                              justify-center mb-3">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor"
                     viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-white">Set New Password</h1>
              <p className="text-blue-200 text-sm mt-1">Choose a strong password.</p>
            </div>

            <div className="px-8 py-8">
              {pageState === "verifying" && renderVerifying()}
              {pageState === "invalid"   && renderInvalid()}
              {pageState === "success"   && renderSuccess()}
              {pageState === "valid"     && renderForm()}

              {/* Back to login */}
              {pageState !== "success" && (
                <div className="mt-6 pt-5 border-t border-gray-100 text-center">
                  <Link
                    to="/"
                    className="text-sm text-gray-400 hover:text-blue-900
                               transition-colors flex items-center justify-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor"
                         viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
                    </svg>
                    Back to Login
                  </Link>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
  